import { useState } from "react";
import {
  Archive,
  ArrowDownToLine,
  Check,
  Copy,
  Download,
  FlipHorizontal2,
  FlipVertical2,
  Image as ImageIcon,
  Link2,
  Loader2,
  QrCode,
  RefreshCw,
  RotateCw,
  Share2,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  Undo2,
  Upload,
  X,
} from "lucide-react";
import { formatBytes } from "../lib/utils.js";

const TABS = [
  { id: "convert", label: "转换", icon: RefreshCw },
  { id: "compress", label: "压缩", icon: Archive },
  { id: "share", label: "分享", icon: Share2 },
  { id: "edit", label: "编辑", icon: SlidersHorizontal },
];

export default function ToolsPanel(props) {
  const {
    kind,
    toolTab,
    setToolTab,
    targets,
    activeTarget,
    setActiveTarget,
    convertOpts,
    setConvertOpts,
    runConversion,
    busy,
    record,
    currentName,
    setCurrentName,
    compressed,
    runCompress,
    compressOpts,
    setCompressOpts,
    qrUrl,
    shareLink,
    copyShare,
    downloadQr,
    systemShare,
    cloudReady,
    cloudBusy,
    cloudShare,
    cloudQrUrl,
    cloudExpiresDays,
    setCloudExpiresDays,
    runCloudShare,
    copyCloudShare,
    downloadCloudQr,
    pdfEdit,
    imageEdits,
    setImageEdits,
    resetImageEdits,
    outputs,
    downloadOutput,
  } = props;

  return (
    <aside className="tools-panel">
      <div className="tools-tabs" role="tablist">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              type="button"
              role="tab"
              key={tab.id}
              className={`tools-tab ${toolTab === tab.id ? "active" : ""}`}
              onClick={() => setToolTab(tab.id)}
            >
              <Icon size={16} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div className="tools-content">
        {toolTab === "convert" && (
          <ConvertTab
            kind={kind}
            targets={targets}
            activeTarget={activeTarget}
            setActiveTarget={setActiveTarget}
            convertOpts={convertOpts}
            setConvertOpts={setConvertOpts}
            runConversion={runConversion}
            busy={busy}
            outputs={outputs}
            downloadOutput={downloadOutput}
          />
        )}
        {toolTab === "compress" && (
          <CompressTab
            kind={kind}
            busy={busy}
            record={record}
            compressed={compressed}
            runCompress={runCompress}
            compressOpts={compressOpts}
            setCompressOpts={setCompressOpts}
          />
        )}
        {toolTab === "share" && (
          <ShareTab
            busy={busy}
            qrUrl={qrUrl}
            shareLink={shareLink}
            copyShare={copyShare}
            downloadQr={downloadQr}
            systemShare={systemShare}
            record={record}
            cloudReady={cloudReady}
            cloudBusy={cloudBusy}
            cloudShare={cloudShare}
            cloudQrUrl={cloudQrUrl}
            cloudExpiresDays={cloudExpiresDays}
            setCloudExpiresDays={setCloudExpiresDays}
            runCloudShare={runCloudShare}
            copyCloudShare={copyCloudShare}
            downloadCloudQr={downloadCloudQr}
          />
        )}
        {toolTab === "edit" && (
          <EditTab
            kind={kind}
            currentName={currentName}
            setCurrentName={setCurrentName}
            pdfEdit={pdfEdit}
            imageEdits={imageEdits}
            setImageEdits={setImageEdits}
            resetImageEdits={resetImageEdits}
            record={record}
          />
        )}
      </div>
    </aside>
  );
}

function ConvertTab({
  kind,
  targets,
  activeTarget,
  setActiveTarget,
  convertOpts,
  setConvertOpts,
  runConversion,
  busy,
  outputs,
  downloadOutput,
}) {
  const target = targets.find((t) => t.id === activeTarget);
  return (
    <div className="tool-section">
      <div className="tool-section-head">
        <h4>格式转换</h4>
        <span className="tool-hint">选择目标格式</span>
      </div>
      <div className="target-list">
        {targets.map((t) => (
          <button
            type="button"
            key={t.id}
            className={`target-row ${activeTarget === t.id ? "active" : ""}`}
            onClick={() => setActiveTarget(t.id)}
          >
            <span className="target-radio">{activeTarget === t.id && <Check size={13} />}</span>
            <span>
              <strong>{t.label}</strong>
              <small>{t.desc}</small>
            </span>
          </button>
        ))}
      </div>

      {target && target.options && (
        <div className="option-group">
          {target.options.includes("format") && (
            <label className="field">
              <span>图片格式</span>
              <select
                value={convertOpts.format}
                onChange={(e) => setConvertOpts({ ...convertOpts, format: e.target.value })}
              >
                <option value="png">PNG（无损）</option>
                <option value="jpg">JPG（高质量）</option>
                <option value="webp">WebP（网页优化）</option>
              </select>
            </label>
          )}
          {target.options.includes("scale") && (
            <label className="field">
              <span>导出清晰度</span>
              <select
                value={convertOpts.scale}
                onChange={(e) => setConvertOpts({ ...convertOpts, scale: Number(e.target.value) })}
              >
                <option value={1}>标准 (1x)</option>
                <option value={2}>高清 (2x)</option>
                <option value={3}>超清 (3x)</option>
              </select>
            </label>
          )}
          {target.options.includes("quality") && (
            <label className="field">
              <span>图片质量：{Math.round(convertOpts.quality * 100)}%</span>
              <input
                type="range"
                min="0.4"
                max="1"
                step="0.05"
                value={convertOpts.quality}
                onChange={(e) => setConvertOpts({ ...convertOpts, quality: Number(e.target.value) })}
              />
            </label>
          )}
          {target.options.includes("pages") && (
            <label className="check-field">
              <input
                type="checkbox"
                checked={convertOpts.allPages}
                onChange={(e) => setConvertOpts({ ...convertOpts, allPages: e.target.checked })}
              />
              <span>导出全部页面</span>
            </label>
          )}
        </div>
      )}

      <button
        type="button"
        className="btn btn-primary btn-block"
        disabled={Boolean(busy)}
        onClick={() => runConversion(target)}
      >
        {busy ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
        {busy ? busy : target ? `转换为${target.label.replace(/^转/, "")}` : "开始转换"}
      </button>

      {outputs.length > 0 && (
        <div className="output-list">
          <div className="tool-section-head compact">
            <h4>转换结果</h4>
            <span className="tool-hint">{outputs.length} 项</span>
          </div>
          {outputs.map((out) => (
            <div key={out.id} className="output-row">
              <span className="output-icon">
                <Download size={15} />
              </span>
              <span className="output-copy">
                <strong title={out.name}>{out.name}</strong>
                <small>{formatBytes(out.size)}</small>
              </span>
              <button
                type="button"
                className="icon-btn"
                title="下载"
                onClick={() => downloadOutput(out)}
              >
                <ArrowDownToLine size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CompressTab({ kind, busy, record, compressed, runCompress, compressOpts, setCompressOpts }) {
  const canRepack = ["docx", "xlsx", "pptx", "sheet"].includes(kind);
  return (
    <div className="tool-section">
      <div className="tool-section-head">
        <h4>文件压缩</h4>
        <span className="tool-hint">{record ? `原大小 ${formatBytes(record.size)}` : ""}</span>
      </div>
      {kind === "image" && (
        <>
          <label className="field">
            <span>目标宽度：{compressOpts.maxWidth}px</span>
            <input
              type="range"
              min="320"
              max="4000"
              step="40"
              value={compressOpts.maxWidth}
              onChange={(e) => setCompressOpts({ ...compressOpts, maxWidth: Number(e.target.value) })}
            />
          </label>
          <label className="field">
            <span>JPEG 质量：{Math.round(compressOpts.quality * 100)}%</span>
            <input
              type="range"
              min="0.1"
              max="0.95"
              step="0.05"
              value={compressOpts.quality}
              onChange={(e) => setCompressOpts({ ...compressOpts, quality: Number(e.target.value) })}
            />
          </label>
        </>
      )}
      {kind === "pdf" && (
        <>
          <label className="field">
            <span>图片质量：{Math.round(compressOpts.quality * 100)}%</span>
            <input
              type="range"
              min="0.35"
              max="0.9"
              step="0.05"
              value={compressOpts.quality}
              onChange={(e) => setCompressOpts({ ...compressOpts, quality: Number(e.target.value) })}
            />
          </label>
          <label className="field">
            <span>渲染比例：{compressOpts.scale}x</span>
            <input
              type="range"
              min="1"
              max="2"
              step="0.1"
              value={compressOpts.scale}
              onChange={(e) => setCompressOpts({ ...compressOpts, scale: Number(e.target.value) })}
            />
          </label>
        </>
      )}
      {canRepack && (
        <p className="tool-note">
          此格式将通过最高压缩级别重新打包内部文件，适合包含较多 XML 资源的 Office 文档。
        </p>
      )}
      <button
        type="button"
        className="btn btn-primary btn-block"
        disabled={Boolean(busy)}
        onClick={runCompress}
      >
        {busy ? <Loader2 size={16} className="spin" /> : <Archive size={16} />}
        {busy ? busy : "开始压缩"}
      </button>
      {compressed && (
        <div className="compress-result">
          <div className="result-line">
            <span>压缩前</span>
            <strong>{formatBytes(record.size)}</strong>
          </div>
          <div className="result-line">
            <span>压缩后</span>
            <strong>{formatBytes(compressed.size)}</strong>
          </div>
          <div className="result-line total">
            <span>节省</span>
            <strong>
              {record.size
                ? `${Math.max(0, Math.round((1 - compressed.size / record.size) * 100))}%`
                : "-"}
            </strong>
          </div>
        </div>
      )}
    </div>
  );
}

function ShareTab({
  busy,
  qrUrl,
  shareLink,
  copyShare,
  downloadQr,
  systemShare,
  record,
  cloudReady,
  cloudBusy,
  cloudShare,
  cloudQrUrl,
  cloudExpiresDays,
  setCloudExpiresDays,
  runCloudShare,
  copyCloudShare,
  downloadCloudQr,
}) {
  const [mode, setMode] = useState("local");
  const activeLink = mode === "cloud" && cloudShare ? cloudShare.link : shareLink;
  const activeQr = mode === "cloud" ? cloudQrUrl : qrUrl;

  return (
    <div className="tool-section">
      <div className="tool-section-head">
        <h4>分享与二维码</h4>
        <span className="tool-hint">生成专属访问链接</span>
      </div>

      <div className="segmented" role="tablist" aria-label="分享方式">
        <button
          type="button"
          role="tab"
          className={mode === "local" ? "active" : ""}
          onClick={() => setMode("local")}
        >
          本地链接
        </button>
        <button
          type="button"
          role="tab"
          className={mode === "cloud" ? "active" : ""}
          onClick={() => setMode("cloud")}
        >
          云端分享
        </button>
      </div>

      {mode === "local" ? (
        <>
          <label className="field">
            <span>分享链接</span>
            <div className="link-box">
              <input readOnly value={shareLink || "正在生成链接..."} onFocus={(e) => e.target.select()} />
              <button type="button" className="icon-btn" title="复制链接" onClick={copyShare}>
                <Copy size={16} />
              </button>
            </div>
          </label>
          <div className="qr-box">
            {qrUrl ? (
              <img src={qrUrl} alt="本地分享二维码" />
            ) : (
              <div className="qr-placeholder">
                {busy ? <Loader2 size={22} className="spin" /> : <QrCode size={22} />}
                {busy ? "生成中..." : "二维码"}
              </div>
            )}
          </div>
          <div className="share-actions">
            <button type="button" className="btn" onClick={downloadQr} disabled={!qrUrl}>
              <Download size={16} />
              二维码图片
            </button>
            <button type="button" className="btn" onClick={systemShare} disabled={!record}>
              <Share2 size={16} />
              系统分享
            </button>
          </div>
          <p className="tool-note">链接使用文件 ID 在本机文档库中定位内容，仅限当前浏览器。</p>
        </>
      ) : (
        <>
          {cloudReady === null && (
            <p className="tool-note">
              <Loader2 size={14} className="spin" /> 正在连接云端服务...
            </p>
          )}
          {cloudReady === false && (
            <p className="tool-note">云端服务未连接；部署到 Cloudflare 后即可在此上传并生成跨设备链接。</p>
          )}
          {cloudReady === true && !cloudShare && (
            <>
              <label className="field">
                <span>链接有效期</span>
                <select
                  value={cloudExpiresDays}
                  onChange={(e) => setCloudExpiresDays(Number(e.target.value))}
                >
                  <option value={7}>7 天</option>
                  <option value={30}>30 天</option>
                  <option value={0}>永久有效</option>
                </select>
              </label>
              <button
                type="button"
                className="btn btn-primary btn-block"
                disabled={cloudBusy}
                onClick={runCloudShare}
              >
                {cloudBusy ? <Loader2 size={16} className="spin" /> : <Upload size={16} />}
                {cloudBusy ? "正在上传..." : "上传并生成云端链接"}
              </button>
              <p className="tool-note">文件保存到 Cloudflare R2，元数据与分享记录写入 D1。</p>
            </>
          )}
          {cloudReady === true && cloudShare && (
            <>
              <label className="field">
                <span>云端分享链接</span>
                <div className="link-box">
                  <input readOnly value={cloudShare.link} onFocus={(e) => e.target.select()} />
                  <button type="button" className="icon-btn" title="复制链接" onClick={copyCloudShare}>
                    <Copy size={16} />
                  </button>
                </div>
              </label>
              <div className="qr-box">
                {cloudQrUrl ? (
                  <img src={cloudQrUrl} alt="云端分享二维码" />
                ) : (
                  <div className="qr-placeholder">
                    <QrCode size={22} />
                    二维码
                  </div>
                )}
              </div>
              <div className="share-actions">
                <button type="button" className="btn" onClick={downloadCloudQr} disabled={!cloudQrUrl}>
                  <Download size={16} />
                  二维码图片
                </button>
                <button type="button" className="btn" onClick={copyCloudShare} disabled={!cloudShare}>
                  <Copy size={16} />
                  复制链接
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function EditTab({
  kind,
  currentName,
  setCurrentName,
  pdfEdit,
  imageEdits,
  setImageEdits,
  resetImageEdits,
  record,
}) {
  return (
    <div className="tool-section">
      <div className="tool-section-head">
        <h4>编辑工具</h4>
        <span className="tool-hint">操作实时预览</span>
      </div>
      <label className="field">
        <span>文件名称</span>
        <input
          type="text"
          value={currentName || ""}
          onChange={(e) => setCurrentName(e.target.value)}
        />
      </label>

      {kind === "pdf" && (
        <div className="edit-group">
          <p className="tool-note">当前操作：{pdfEdit?.selectedLabel}</p>
          <div className="btn-grid two">
            <button type="button" className="btn" onClick={pdfEdit?.rotateSelected}>
              <RotateCw size={15} />
              旋转 90°
            </button>
            <button type="button" className="btn btn-danger" onClick={pdfEdit?.deleteSelected}>
              <Trash2 size={15} />
              删除本页
            </button>
          </div>
          <div className="btn-grid two">
            <button type="button" className="btn" onClick={pdfEdit?.moveUp}>
              ↑ 上移
            </button>
            <button type="button" className="btn" onClick={pdfEdit?.moveDown}>
              ↓ 下移
            </button>
          </div>
          <button type="button" className="btn btn-block" onClick={pdfEdit?.resetPages}>
            <Undo2 size={15} />
            恢复原始页序
          </button>
          <p className="tool-note">页面操作会直接写入当前 PDF。</p>
        </div>
      )}

      {kind === "image" && (
        <div className="edit-group">
          <div className="btn-grid two">
            <button type="button" className="btn" onClick={() => setImageEdits({ ...imageEdits, rotation: (imageEdits.rotation + 90) % 360 })}>
              <RotateCw size={15} />
              旋转
            </button>
            <button type="button" className="btn" onClick={() => setImageEdits({ ...imageEdits, flipH: !imageEdits.flipH })}>
              <FlipHorizontal2 size={15} />
              水平翻转
            </button>
          </div>
          <div className="btn-grid two">
            <button type="button" className="btn" onClick={() => setImageEdits({ ...imageEdits, flipV: !imageEdits.flipV })}>
              <FlipVertical2 size={15} />
              垂直翻转
            </button>
            <button type="button" className="btn" onClick={resetImageEdits}>
              <Undo2 size={15} />
              重置
            </button>
          </div>
          {[
            ["brightness", "亮度", 30, 180],
            ["contrast", "对比度", 30, 180],
            ["saturate", "饱和度", 0, 220],
          ].map(([key, label, min, max]) => (
            <label className="field" key={key}>
              <span>{label}：{imageEdits?.[key]}</span>
              <input
                type="range"
                min={min}
                max={max}
                value={imageEdits?.[key]}
                onChange={(e) => setImageEdits({ ...imageEdits, [key]: Number(e.target.value) })}
              />
            </label>
          ))}
          <p className="tool-note">编辑效果会同步到预览、转换与下载结果。</p>
        </div>
      )}

      {["docx", "xlsx", "pptx", "sheet", "text"].includes(kind) && (
        <div className="edit-group">
          <div className="edit-hint">
            <Sparkles size={17} />
            <p>当前文件支持在预览区直接编辑：{kind === "docx" && "文字排版、标题与列表"}
              {kind === "xlsx" && "单元格内容、行列增删"}
              {kind === "sheet" && "表格单元格内容"}
              {kind === "pptx" && "逐页文本框内容"}
              {kind === "text" && "纯文本内容"}</p>
          </div>
          <p className="tool-note">当前编辑内容会写入文档库，并用于后续格式导出。</p>
        </div>
      )}
    </div>
  );
}
